import { useAppSelector } from '@/app/redux/hooks';
import { useOrganisation } from '@/app/redux/hooks/organisation';
import { selectUserId } from '@/app/redux/slices/sessionSlice';
import { UNKNOWN_ERROR_MESSAGE } from '@/common/ui/UnknownErrorMessage';
import UpgradeButton from '@/common/ui/UpgradeButton';
import {
  getBillingCycleLabel,
  getNextBillDate,
  getPlanDescription,
  getPlanIcon,
  getTrialDaysRemaining,
  isLimitedPlan,
  PLAN_DETAILS,
} from '@/common/utils/license';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import { isUserAdmin } from '@/common/utils/organisation';
import {
  SectionContainer,
  SectionContent,
  SectionHeader,
} from '@/dashboard/SettingsView/Billing/BillingPageStyles';
import ManageSubscriptionDialogue from '@/dashboard/SettingsView/Billing/ManageSubscriptionDialogue';
import UpdateBillingAddressDialogue from '@/dashboard/SettingsView/Billing/UpdateBillingAddressDialogue';
import api from '@/db/apiClient';
import { Badge } from '@shadcn/components/badge';
import { Button } from '@shadcn/components/button';
import { cn } from '@shadcn/lib/utils';
import dayjs from 'dayjs';
import { capitalize } from 'lodash';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

export default function BillingOverview() {
  const navigate = useNavigate();

  const { organisation } = useOrganisation();

  const userId = useAppSelector(selectUserId);

  const license = organisation?.license;
  const licensePlan = license?.plan ?? 'free';
  const licenseStatus = license?.status ?? 'inactive';
  const trialEndDate = license?.trialEndDate ?? 0;
  const billingCycle = license?.billingCycle ?? '1year';
  const billingCycleAnchor = license?.billingCycleAnchor ?? trialEndDate;
  const seatCount = license?.seatCount ?? 0; // 0 means unlimited
  const scheduledForCancellation = license?.scheduledForCancellation ?? false;
  const planDetails = PLAN_DETAILS[licensePlan];

  const [
    isUpdateBillingAddressDialogueOpen,
    setIsUpdateBillingAddressDialogueOpen,
  ] = useState(false);

  const trialDaysRemaining = useMemo(() => {
    if (licensePlan !== 'trial') return 0;
    return getTrialDaysRemaining(trialEndDate);
  }, [licensePlan, trialEndDate]);

  const nextBillDate = useMemo(() => {
    return dayjs(getNextBillDate(billingCycle, billingCycleAnchor)).format(
      'DD MMM YYYY',
    );
  }, [billingCycleAnchor, billingCycle]);

  const planDescription = useMemo(() => {
    return getPlanDescription(licensePlan, seatCount);
  }, [licensePlan, seatCount]);

  const handleUpgradeClick = () => {
    trackMixpanelEvent('Clicked Upgrade from Billing', {
      source: 'billing_overview',
      currentPlan: licensePlan,
      trialDaysRemaining:
        licensePlan === 'trial' ? trialDaysRemaining : undefined,
    });
    navigate('/pricing');
  };

  async function handleResetLicense() {
    if (!organisation) {
      console.error('Missing org');
      toast.error('Failed to reset license', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
      return;
    }
    try {
      await api.asEducator.resetOrganisationLicense(organisation.id);
      toast.success('License reset');
    } catch (e) {
      console.error('Error resetting license:', e);
      toast.error('Failed to reset license', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
    }
  }

  return (
    <SectionContainer>
      <SectionHeader>
        <div className="flex gap-3 items-center">
          {isLimitedPlan(licensePlan) && (
            <img
              src={getPlanIcon(licensePlan)}
              alt={planDetails.name}
              className="w-10 h-10 rounded-full"
            />
          )}
          <span className="font-medium text-lg">{planDetails.name}</span>
          <Badge
            variant={licenseStatus === 'past_due' ? 'destructive' : 'outline'}
            className="text-xs h-fit"
          >
            {capitalize(licenseStatus.replace('_', ' '))}
          </Badge>
        </div>
        <span className="text-sm text-muted-foreground mt-2">
          {planDescription}
        </span>
      </SectionHeader>

      <SectionContent>
        {licensePlan === 'trial' ? (
          <div className="flex items-center text-sm font-medium gap-1">
            <span
              className={cn(
                'opacity-90 text-white px-2 py-0.5 rounded-md',
                trialDaysRemaining > 0 ? 'bg-primary' : 'bg-yellow-500',
              )}
            >
              {trialDaysRemaining}
            </span>{' '}
            day{trialDaysRemaining === 1 ? '' : 's'} left on trial
          </div>
        ) : isLimitedPlan(licensePlan) ? (
          <div className="flex gap-8 text-sm font-medium">
            <div className="flex flex-col gap-1">
              <span>Billed</span>
              <span>Next invoice</span>
            </div>
            <div className="flex flex-col gap-1">
              <span className="font-bold capitalize">
                {getBillingCycleLabel(billingCycle)}
              </span>
              {scheduledForCancellation ? (
                <span className="text-yellow-500 font-medium">
                  Scheduled for cancellation on {nextBillDate}
                </span>
              ) : (
                <span className="font-bold">{nextBillDate}</span>
              )}
            </div>
          </div>
        ) : null}

        <div className="flex items-center gap-2">
          {licensePlan === 'trial' || licensePlan === 'free' ? (
            <UpgradeButton onClick={handleUpgradeClick} />
          ) : isUserAdmin(organisation, userId) &&
            isLimitedPlan(licensePlan) ? (
            <ManageSubscriptionDialogue
              onUpdateBillingAddress={() =>
                setIsUpdateBillingAddressDialogueOpen(true)
              }
            >
              <Button disabled={scheduledForCancellation}>
                Manage Subscription
              </Button>
            </ManageSubscriptionDialogue>
          ) : null}
          {process.env.NODE_ENV === 'development' && (
            <Button variant="outline" onClick={handleResetLicense}>
              Reset License
            </Button>
          )}
        </div>
      </SectionContent>

      <UpdateBillingAddressDialogue
        isOpen={isUpdateBillingAddressDialogueOpen}
        onClose={() => setIsUpdateBillingAddressDialogueOpen(false)}
      />
    </SectionContainer>
  );
}
